import env from "env";
import userService from "./UserService";
import store from "store";

const ONESIGNAL_APP_ID = window.ONESIGNAL_APP_ID || env.ONESIGNAL_APP_ID;
const CACHE_KEY_DISABLE_NOTIFICATION = 'disable_notification';

const addScript = () => {
    return new Promise((resolve) => {
        if (!document.querySelector("#onesignal_sdk")) {
            let script = document.createElement("script");
            script.id = "onesignal_sdk";
            script.src = "https://cdn.onesignal.com/sdks/OneSignalSDK.js";
            document.head.appendChild(script);
        }
        let timeoutID = 0;
        const handler = () => {
            if (window.OneSignal) {
                clearInterval(timeoutID);
                resolve(window.OneSignal);
            }
        };
        timeoutID = setInterval(handler, 500);
        handler();
    })
}

export default {
    addScript,
    ready() {
        return addScript().then((OneSignal) => {
            if (!window._oneSignal_ready) {
                if (env.IS_DEVELOPMENT) {
                    OneSignal.log.setLevel('trace');
                }

                OneSignal.SERVICE_WORKER_PARAM = { scope: '/push/' };
                OneSignal.SERVICE_WORKER_PATH = 'push/OneSignalSDKWorker.js'
                OneSignal.SERVICE_WORKER_UPDATER_PATH = 'push/OneSignalSDKWorker.js'

                try {
                    OneSignal.init({
                        appId: ONESIGNAL_APP_ID,
                        welcomeNotification: {
                            disable: true,
                        },
                        persistNotification: false,
                        autoResubscribe: false,
                        autoRegister: false,
                        notifyButton: {
                            enable: false,
                        },
                    });
                } catch (e) {
                    console.error(e);
                    return Promise.reject(e);
                }

                window._oneSignal_ready = true;
            }

            return Promise.resolve(OneSignal);
        })
    },

    subscribe(existUUDI, force = false) {
      const isDisableNotification = store.get(CACHE_KEY_DISABLE_NOTIFICATION);
      if (isDisableNotification && !force) {
          return Promise.resolve();
      }

      this.ready()
          .then((OneSignal) => {
              if (OneSignal.isPushNotificationsSupported()) {
                  return OneSignal.isPushNotificationsEnabled();
              }
              return Promise.reject(new Error('Push notifications are not supported'));
          })
          .then(() => {
              return OneSignal.getNotificationPermission();
          })
          .then((permission) => {
            if (permission === "granted") {
                return Promise.resolve();
            }
            return Promise.resolve(new Error("Notification permission is not granted"));
          })
          .then(() => {
              return OneSignal.getUserId();
          })
          .then((uuid) => {
              if (uuid && uuid !== existUUDI) {
                  return (new userService()).onesignal(uuid, ONESIGNAL_APP_ID)
              }
              if (!uuid) {
                  OneSignal.showSlidedownPrompt();
                  return new Promise((resolve, reject) => {
                      OneSignal.on('subscriptionChange', (isSubscribed) => {
                          if (isSubscribed) {
                              resolve();
                              return;
                          }
                          reject(new Error("Push notifications are not supported"));
                      });
                  })
                  .then(() => {
                      return this.subscribe(existUUDI);
                  })
              }
              if (uuid && uuid === existUUDI) {
                  return Promise.resolve();
              }
              Promise.reject(new Error("Push notifications are not supported"));
          })
          .catch(error => {
              if (error instanceof Error) {
                  console.error(error.message);
              }
              return Promise.reject(error);
          })
    },

    ifNotificationsPermissionGranted() {
        if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                return true;
            }
        }
        return false;
    },

    isOneSignalRuntimeReady() {
        return !!(window._oneSignal_ready);
    },

    isPushNotificationsSupported() {
        if (window.OneSignal) {
            return OneSignal.isPushNotificationsSupported();
        }
        return false;
    },

    isPushNotificationsEnabled(timeout = 2000) {
        if (window.OneSignal) {
            return Promise.race([OneSignal.isPushNotificationsEnabled(), new Promise(resolve => {
                return setTimeout(() => {
                    resolve(false);
                }, timeout);
            })])
        }
        return Promise.resolve(false);
    },

    isSubscribedNotification(timeout = 2000) {
        if (window.OneSignal) {
            const getUserID = () => {
                return Promise.race([OneSignal.getUserId(), new Promise(resolve => {
                    return setTimeout(() => {
                        resolve(false);
                    }, timeout);
                })])
            };

            return Promise.all([
                (new userService()).me(true),
                getUserID(),
            ]).then(([profile, uuid]) => {
               return profile.subscribed && profile.subscribed === uuid;
            });
        }

        return Promise.resolve(false);
    },

    register(OneSignal) {
        OneSignal.isPushNotificationsEnabled(function(isEnabled) {
            if (isEnabled) {
                console.log("Push notifications are enabled!");
            }
            else {
                console.log("Push notifications are not enabled yet.");
                OneSignal.showNativePrompt();
            }
        });
        OneSignal.push(["getNotificationPermission", function(permission) {
            console.log("Site Notification Permission:", permission);
            // (Output) Site Notification Permission: default
            if (permission === "granted") {
                OneSignal.showNativePrompt();
            }
        }]);

        const isPushSupported = OneSignal.isPushNotificationsSupported();
        if (isPushSupported) {
            console.log('Push notifications are supported');
        } else {
            console.log('Push notifications are not supported');
        }

        function isPushNotificationsEnabledVerbose() {
            console.log('isPushNotificationsEnabledVerbose()');
            Promise.all([
                OneSignal.isPushNotificationsEnabled(),
                OneSignal.getUserId(),
                OneSignal.getRegistrationId(),
                OneSignal.getNotificationPermission(),
                OneSignal.isOptedOut(),
                OneSignal.context.serviceWorkerManager.getActiveState()
            ])
                .then(([isSubscribed, userId, registrationId, notificationPermission, optedOut, serviceWorkerActive]) => {
                    console.log('Is Completely Subscribed:', isSubscribed);
                    console.log('');
                    console.log('What is our OneSignal user ID?', userId);
                    console.log('What is our push subscription token?', registrationId);
                    console.log('What is the notification permission status?', notificationPermission);
                    console.log('Are you manually opted out?', optedOut);
                    console.log("Is a service worker registered and active? (should be false on Safari, otherwise should be 'Worker A (Main)')?", serviceWorkerActive);
                    console.log('What is the current URL of this page?', location.href);
                    console.log("What environment does OneSignal think it's in?", OneSignal.sdkEnvironment.getWindowEnv());
                })
                .catch(e => {
                    console.error("Issue determining whether push is enabled:", e);
                });
        }
        isPushNotificationsEnabledVerbose();

        OneSignal.showNativePrompt();
        OneSignal.showCategorySlidedown();
    }
}
